(() => {
  const MAX_BYTES = 100000000;
  const EXTENSIONS = new Set(['stl', '3mf', 'step', 'stp', 'obj', 'pdf', 'jpg', 'jpeg', 'png']);
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (window.abxTurnstilePromise) return window.abxTurnstilePromise;
    window.abxTurnstilePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = setTimeout(() => fail(), 20000);
      const fail = () => {
        clearTimeout(timeout);
        script.remove();
        window.abxTurnstilePromise = null;
        reject(new Error('verification_error'));
      };
      window.abxTurnstileLoaded = () => {
        clearTimeout(timeout);
        resolve(window.turnstile);
      };
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=abxTurnstileLoaded';
      script.async = true;
      script.onerror = fail;
      document.head.appendChild(script);
    });
    return window.abxTurnstilePromise;
  };
  const initialize = root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const form = root.closest('form');
    const fileInput = root.querySelector('input[type="file"]');
    const submit = form.querySelector('button[type="submit"]');
    const status = root.querySelector('[data-upload-status]');
    const filename = root.querySelector('[data-upload-filename]');
    const verification = root.querySelector('[data-upload-verification]');
    const remove = root.querySelector('[data-upload-remove]');
    const retry = root.querySelector('[data-upload-retry]');
    const progress = root.querySelector('[data-upload-progress]');
    const messages = JSON.parse(root.querySelector('[data-upload-messages]').textContent);
    const fields = {
      uploadId: root.querySelector('[data-upload-id]'),
      projectId: root.querySelector('[data-upload-project]'),
      filename: root.querySelector('[data-upload-name]'),
      sizeBytes: root.querySelector('[data-upload-bytes]')
    };
    let token = '';
    let widget;
    let busy = false;
    let uploaded = null;
    let generation = 0;
    const show = (key, error = false, suffix = '') => {
      status.hidden = false;
      status.dataset.state = error ? 'error' : 'success';
      status.textContent = (messages[key] || messages.failed) + suffix;
    };
    const persist = value => {
      try {
        if (value) sessionStorage.setItem(root.dataset.storageKey, JSON.stringify({ ...value, savedAt: Date.now() }));
        else sessionStorage.removeItem(root.dataset.storageKey);
      } catch {}
    };
    const setReference = value => {
      uploaded = value;
      for (const [key, input] of Object.entries(fields)) {
        input.value = value ? String(value[key]) : '';
        input.disabled = !value;
      }
    };
    const destroyWidget = () => {
      token = '';
      if (widget !== undefined && window.turnstile) window.turnstile.remove(widget);
      widget = undefined;
      verification.hidden = true;
      retry.hidden = true;
    };
    const validate = file => {
      if (!EXTENSIONS.has(file.name.split('.').pop().toLowerCase()) || !file.name.includes('.')) return 'unsupported';
      if (file.size > MAX_BYTES) return 'too_large';
      if (file.size === 0) return 'empty';
      if (new TextEncoder().encode(file.name).length > 240 || /[\x00-\x1f\x7f/\\]/.test(file.name)) return 'filename';
      return '';
    };
    const prepareVerification = async () => {
      const revision = generation;
      if (!fileInput.files[0] || validate(fileInput.files[0]) || uploaded) return;
      token = '';
      retry.hidden = true;
      show('verifying');
      verification.hidden = false;
      try {
        const api = await loadTurnstile();
        if (revision !== generation || !root.isConnected || !fileInput.files[0]) return;
        if (widget !== undefined) { api.reset(widget); return; }
        widget = api.render(verification, {
          sitekey: root.dataset.sitekey,
          action: 'project_upload',
          theme: 'dark',
          size: verification.getBoundingClientRect().width < 300 ? 'compact' : 'normal',
          'response-field': false,
          callback: value => { token = value; retry.hidden = true; if (!busy) show('verified'); },
          'expired-callback': () => { token = ''; if (!busy) show('expired', true); },
          'error-callback': () => { token = ''; if (!busy) show('verification_error', true); retry.hidden = false; },
          'timeout-callback': () => { token = ''; if (!busy) show('expired', true); retry.hidden = false; }
        });
        if (!token) show('ready');
      } catch {
        if (revision !== generation) return;
        show('verification_error', true);
        retry.hidden = false;
      }
    };
    const clear = () => {
      generation++;
      fileInput.value = '';
      fileInput.setCustomValidity('');
      fileInput.removeAttribute('aria-invalid');
      setReference(null);
      persist(null);
      destroyWidget();
      status.hidden = true;
      filename.hidden = true;
      remove.hidden = true;
      progress.hidden = true;
    };
    const setBusy = value => {
      busy = value;
      form.setAttribute('aria-busy', String(value));
      submit.disabled = value;
      submit.textContent = value ? messages.uploading : messages.submit;
      fileInput.disabled = value;
      remove.disabled = value;
      retry.disabled = value;
    };
    const upload = file => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', root.dataset.endpoint);
      xhr.timeout = 300000;
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
      xhr.setRequestHeader('X-File-Size', String(file.size));
      xhr.setRequestHeader('X-Turnstile-Token', token);
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const percent = Math.round(event.loaded / event.total * 100);
          progress.value = percent;
          show('uploading', false, ' ' + percent + '%');
        }
      };
      xhr.onload = () => {
        let result;
        try { result = JSON.parse(xhr.responseText); } catch { reject(new Error('failed')); return; }
        if (xhr.status !== 201 || result.ok !== true) {
          const code = result.error?.code;
          const key = code === 'file_too_large' ? 'too_large' : code === 'file_type_not_allowed' ? 'unsupported' : code === 'rate_limit_exceeded' ? 'rate_limit' : code?.startsWith('verification_') ? 'verification_error' : 'failed';
          reject(new Error(key));
          return;
        }
        if (!UUID.test(result.uploadId) || !UUID.test(result.projectId) || result.sizeBytes !== file.size || result.filename !== file.name) {
          reject(new Error('failed'));
          return;
        }
        resolve({ uploadId: result.uploadId, projectId: result.projectId, filename: result.filename, sizeBytes: result.sizeBytes });
      };
      xhr.onerror = () => reject(new Error('failed'));
      xhr.ontimeout = () => reject(new Error('timeout'));
      xhr.onabort = () => reject(new Error('failed'));
      xhr.send(file);
    });
    fileInput.disabled = false;
    try {
      const stored = JSON.parse(sessionStorage.getItem(root.dataset.storageKey) || 'null');
      if (root.dataset.posted === 'true') persist(null);
      else if (stored && Date.now() - stored.savedAt < 3600000 && UUID.test(stored.uploadId) && UUID.test(stored.projectId) && typeof stored.filename === 'string' && Number.isSafeInteger(stored.sizeBytes) && stored.sizeBytes > 0 && stored.sizeBytes <= MAX_BYTES) {
        setReference({ uploadId: stored.uploadId, projectId: stored.projectId, filename: stored.filename, sizeBytes: stored.sizeBytes });
        filename.textContent = stored.filename;
        filename.hidden = false;
        remove.hidden = false;
        show('restored');
      } else persist(null);
    } catch {}
    fileInput.addEventListener('change', () => {
      generation++;
      setReference(null);
      persist(null);
      destroyWidget();
      progress.hidden = true;
      fileInput.setCustomValidity('');
      fileInput.removeAttribute('aria-invalid');
      const file = fileInput.files[0];
      if (!file) { clear(); return; }
      filename.textContent = file.name;
      filename.hidden = false;
      remove.hidden = false;
      const error = validate(file);
      if (error) {
        fileInput.setCustomValidity(messages[error]);
        fileInput.setAttribute('aria-invalid', 'true');
        show(error, true);
        return;
      }
      prepareVerification();
    });
    remove.addEventListener('click', () => { clear(); fileInput.focus(); });
    retry.addEventListener('click', prepareVerification);
    form.addEventListener('submit', async event => {
      if (busy) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      const file = fileInput.files[0];
      if (!file || uploaded) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const error = validate(file);
      if (error) { show(error, true); fileInput.focus(); return; }
      if (!token) {
        show('verification_error', true);
        retry.hidden = false;
        status.focus();
        return;
      }
      const submitter = event.submitter || submit;
      setBusy(true);
      progress.hidden = false;
      progress.value = 0;
      show('uploading');
      try {
        const reference = await upload(file);
        setReference(reference);
        persist(reference);
        destroyWidget();
        show('uploaded');
        setBusy(false);
        submit.textContent = messages.sending;
        form.requestSubmit(submitter);
      } catch (error) {
        setBusy(false);
        token = '';
        if (widget !== undefined && window.turnstile) window.turnstile.reset(widget);
        retry.hidden = false;
        progress.hidden = true;
        show(error.message, true);
        status.focus();
      }
    }, true);
    window.addEventListener('pageshow', () => { if (!busy) submit.textContent = messages.submit; });
  };
  const start = scope => scope.querySelectorAll('[data-abx-upload]').forEach(initialize);
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
